import {
  VegaAccordion,
  VegaButton,
  VegaCard,
  VegaDatePicker,
  VegaFlex,
  VegaGrid,
  VegaInput,
  VegaInputSelect,
} from '@globalpayments/vega-react';

import { ALL_HORIZON_REGIONS, ALL_TRIGGERED_BY } from '../../../types/horizonOptOutTracker';
import type {
  HorizonOptOutDateRange,
  HorizonRegionFilter,
  HorizonTriggeredByFilter,
} from '../../../types/horizonOptOutTracker';
import { horizonRegionOptions, triggeredByOptions } from '../constants';

interface HorizonOptOutFiltersProps {
  applicationSearch: string;
  dateRange: HorizonOptOutDateRange;
  isExpanded: boolean;
  isInvalid: boolean;
  onApply: () => void;
  onApplicationSearchChange: (applicationSearch: string) => void;
  onDateRangeChange: (dateRange: HorizonOptOutDateRange) => void;
  onExpandedChange: (isExpanded: boolean) => void;
  onOptOutReasonChange: (optOutReason: string) => void;
  onRegionChange: (region: HorizonRegionFilter) => void;
  onTriggeredByChange: (triggeredBy: HorizonTriggeredByFilter) => void;
  optOutReason: string;
  region: HorizonRegionFilter;
  summary: string;
  triggeredBy: HorizonTriggeredByFilter;
}

/** Displays draft date, region, application, and opt-out-reason controls for the tracker. */
export const HorizonOptOutFilters = ({
  applicationSearch,
  dateRange,
  isExpanded,
  isInvalid,
  onApply,
  onApplicationSearchChange,
  onDateRangeChange,
  onExpandedChange,
  onOptOutReasonChange,
  onRegionChange,
  onTriggeredByChange,
  optOutReason,
  region,
  summary,
  triggeredBy,
}: HorizonOptOutFiltersProps) => (
  <VegaCard backgroundColor="bg-primary" padding="size-16" variant="border">
    <VegaAccordion
      accordionTitle={`Filters: ${summary}`}
      data-testid="horizon-filter-accordion"
      expand={isExpanded}
      expandIconAlign="right"
      groupId="horizon-opt-out-filters"
      onVegaExpand={(event) => onExpandedChange(event.detail)}
      showExpandIcon>
      <VegaFlex data-testid="horizon-filter-layout" direction="col" gap="size-16" slot="content">
        <VegaGrid
          column={{ default: 1, M: 2 }}
          data-testid="horizon-primary-filter-grid"
          gap="size-12"
          justifyItems="stretch">
          <VegaDatePicker
            allowRepick
            data-testid="horizon-date-range"
            format="YYYY-MM-DD"
            isValid={isInvalid ? false : undefined}
            label="Created date range"
            mode="range"
            onVegaChange={(event) => {
              if (
                Array.isArray(event.detail) &&
                event.detail.length === 2 &&
                event.detail.every((date) => typeof date === 'string')
              ) {
                onDateRangeChange({ endDate: event.detail[1], startDate: event.detail[0] });
              }
            }}
            required
            value={[dateRange.startDate, dateRange.endDate]}
          />
          <VegaInputSelect
            data-testid="horizon-region"
            label="Region"
            onVegaChange={(event) => {
              if (event.detail === ALL_HORIZON_REGIONS || event.detail === 'US' || event.detail === 'UK') {
                onRegionChange(event.detail);
              }
            }}
            required
            selectType="single"
            source={[...horizonRegionOptions]}
            value={region}
          />
        </VegaGrid>
        <VegaGrid
          column={{ default: 1, M: 2, L: 3 }}
          data-testid="horizon-search-filter-grid"
          gap="size-12"
          justifyItems="stretch">
          <VegaInput
            data-testid="horizon-application-search"
            label="Search"
            onVegaChange={(event) => {
              if (typeof event.detail === 'string') onApplicationSearchChange(event.detail);
            }}
            placeholder="App ID, DBA Name, or Email"
            showClearIcon
            value={applicationSearch}
          />
          <VegaInput
            data-testid="horizon-opt-out-reason"
            label="Opt-Out Reason"
            onVegaChange={(event) => {
              if (typeof event.detail === 'string') onOptOutReasonChange(event.detail);
            }}
            placeholder="Search opt-out reasons"
            showClearIcon
            value={optOutReason}
          />
          <VegaInputSelect
            data-testid="horizon-triggered-by"
            label="Triggered By"
            onVegaChange={(event) => {
              if (event.detail === ALL_TRIGGERED_BY || event.detail === 'Worth' || event.detail === 'Non-Worth') {
                onTriggeredByChange(event.detail);
              }
            }}
            required
            selectType="single"
            source={[...triggeredByOptions]}
            value={triggeredBy}
          />
        </VegaGrid>
        <VegaFlex justifyContent={{ default: 'start', M: 'end' }}>
          <VegaButton disabled={isInvalid} label="Apply filters" onVegaClick={onApply} size="small" />
        </VegaFlex>
      </VegaFlex>
    </VegaAccordion>
  </VegaCard>
);
